import React from 'react';
import { useZunelo } from '../../context/ZuneloContext';
import { Modal, Avatar, SlackRichText } from '../../ui/common';

export const MergeViewModal: React.FC = () => {
  const { isMergeModalOpen, setIsMergeModalOpen, mergeSet, items, navigateToItem } = useZunelo();

  if (!isMergeModalOpen) return null;

  const selectedItems = mergeSet.map((id) => items.find((i) => i.id === id)).filter(Boolean);

  // Combine messages chronologically
  const allMessages: Array<{
    who: string;
    initials: string;
    color: string;
    time: string;
    text: string;
    source: string;
    sourceId: string;
    names: Record<string, string>;
  }> = [];

  selectedItems.forEach((it) => {
    if (it && it.messages) {
      const names = Object.fromEntries((it.members ?? []).map((m) => [m.id, m.realName || m.name]));
      it.messages.forEach((m) => {
        allMessages.push({
          who: m.user.realName || m.user.name,
          initials: m.user.initials,
          color: m.user.color || 'var(--z-muted)',
          time: m.timeFormatted,
          text: m.text,
          source: it.primary,
          sourceId: it.id,
          names,
        });
      });
    }
  });

  allMessages.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));

  return (
    <Modal isOpen={isMergeModalOpen} onClose={() => setIsMergeModalOpen(false)} width={540}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 18px', borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span
              style={{ fontSize: 15, fontWeight: 700, flex: '1 1 auto', color: 'var(--z-text)' }}
            >
              Conversación combinada
            </span>
            <button
              onClick={() => setIsMergeModalOpen(false)}
              style={{ color: 'var(--z-dim)', fontSize: 13, cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {selectedItems.map((it) => {
              if (!it) return null;
              return (
                <button
                  type="button"
                  key={it.id}
                  aria-label={`Ver ${it.primary}`}
                  onClick={() => {
                    setIsMergeModalOpen(false);
                    navigateToItem('slack', it.id);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 9px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: 'var(--z-accent-soft)',
                    color: 'var(--z-accent-ink)',
                    border: 'none',
                  }}
                >
                  {it.primary}
                </button>
              );
            })}
          </div>
        </div>

        {/* Message Feed */}
        <div
          style={{
            maxHeight: '65vh',
            overflowY: 'auto',
            padding: '14px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {allMessages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: 11,
                padding: '11px 12px',
                borderRadius: 12,
                background: 'var(--z-fill)',
                border: 'none',
              }}
            >
              <Avatar initials={msg.initials} color={msg.color} size={26} />
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    marginBottom: 3,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
                    {msg.who}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--z-faint)' }}>{msg.time}</span>
                  <button
                    type="button"
                    aria-label={`Ver ${msg.source}`}
                    onClick={() => {
                      setIsMergeModalOpen(false);
                      navigateToItem('slack', msg.sourceId);
                    }}
                    style={{
                      marginLeft: 'auto',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--z-accent-3)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                    }}
                  >
                    {msg.source}
                  </button>
                </div>
                <SlackRichText
                  as="div"
                  text={msg.text}
                  names={msg.names}
                  style={{
                    margin: 0,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: 'var(--z-text-3)',
                    textWrap: 'pretty',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
